<template>
  <div id="editTemplate" class="h-full flex flex--column flex__item">
    <div class="flex-shrink bg-white bb">
      <ak-navbar size="tiny" spacing="tiny">
        <ak-navbar-item grow>
          <ak-pack>
            <ak-pack-item>
              <h4 class="mb-none color-grey">{{ template.title }}</h4>
            </ak-pack-item>
            <ak-pack-item>
              <ak-button size="small" @click="editTemplateDetails = true">
                Edit Details
              </ak-button>
              <ak-drawer
                v-model:active="editTemplateDetails"
                show-x
                container="#editTemplate"
                has-backdrop
                position="left"
                :width="400"
              >
                <template #body>
                  <ak-modal-title>Edit Template Details</ak-modal-title>
                  <ak-email-templates-details-form
                    ref="detailsForm"
                    :active-account="activeAccount"
                    :active-agency="activeAgency"
                    :template="template"
                    :users="users"
                    @update-template="onUpdateTemplate"
                  />
                </template>
              </ak-drawer>
            </ak-pack-item>
          </ak-pack>
        </ak-navbar-item>
        <ak-navbar-item>
          <ak-button
            color="primary"
            :is-loading="isSaving"
            @click.prevent="confirmSaveTemplate(template)"
          >
            Save Template
          </ak-button>
        </ak-navbar-item>
      </ak-navbar>
    </div>

    <ak-email-template-builder
      v-model="template"
      v-model:is-loading="isLoading"
      class="flex__item overflow-hidden"
      drawer-container="#editTemplate"
    />

    <ak-send-test-emails-modal
      v-if="users.length"
      v-model:is-active="modalActive"
      :user-profile="userProfile"
      :users="users"
      :send-test="sendTest"
    />
  </div>
</template>

<script>
import { ref, onBeforeMount, defineComponent } from 'vue'
import SendTestEmailsModal from './SendTestEmailsModal.vue'
import EmailTemplatesDetailsForm from './EmailTemplatesDetailsForm.vue'
import EmailTemplateBuilder from '@akerolabs/email-templates/src/components/builder/Builder.vue'
import Navbar from '@akerolabs/akero-vue/src/components/navbar/Navbar.vue'
import NavbarItem from '@akerolabs/akero-vue/src/components/navbar/NavbarItem.vue'
import NavbarButton from '@akerolabs/akero-vue/src/components/navbar/NavbarButton.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { convertTemplate } from './convertOldTemplates'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-templates-edit-template></ak-email-templates-edit-template>
   */
  name: 'AkEmailTemplatesEditTemplate',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [SendTestEmailsModal.name]: SendTestEmailsModal,
    [EmailTemplatesDetailsForm.name]: EmailTemplatesDetailsForm,
    [EmailTemplateBuilder.name]: EmailTemplateBuilder,
    [NavbarButton.name]: NavbarButton,
    [Navbar.name]: Navbar,
    [NavbarItem.name]: NavbarItem,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    id: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Function}
     * @description Checks if a feature is available for the current account
     */
    featureEnabled: {
      default: undefined,
      type: Function,
    },
  },

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const users = ref([])
    const isLoading = ref(true)
    const template = ref({})
    const editTemplateDetails = ref(false)
    const isSaving = ref(false)
    const isSendingTest = ref(false)
    const isConversionComplete = ref(false)
    const modalActive = ref(false)
    const detailsForm = ref(null)

    const onUpdateTemplate = (data) => {
      template.value = data
    }

    const removeChild = (index) => {
      console.log(index)
    }

    /**
     * @returns {Promise}
     * @description Fetches the users from the API
     */
    const fetchUsers = () => {
      return request(`/users/`, {
        params: {
          agency: props.activeAgency._id,
          account: props.activeAccount._id,
          overrideAccess: true,
          status: 'active',
          per_page: 0,
        },
      })
    }

    /**
     * @param {String} id
     * @returns {Promise}
     * @description Returns a promise to fetch the email template from the
     * Akero API
     */
    const fetchTemplate = (id) => {
      return request.get(`/templates/${id}`, {
        params: {
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
        },
      })
    }

    /**
     * @param {Object} template
     * @description Checks that the user has filled the whole email
     * template details form.
     */
    const checkTemplateDetails = async (template) => {
      const isValid = (s) => typeof s === 'string' && s.length >= 1

      if (
        !isValid(template.title) ||
        !isValid(template.from) ||
        !isValid(template.subject)
      ) {
        return false
      }

      return true
    }

    /**
     * @param {Object}
     * @description Shows a confirmation dialog to show the user what email address
     * we are going to send the test email too.
     */
    const confirmSendTest = async (template) => {
      const { email } = props.userProfile.contactDetails

      if (template.blocks.length === 0) {
        DialogProgrammatic.alert({
          title: 'Send Test Email',
          message:
            'You must add content blocks to your email template before sending a test email.',
        })
      } else {
        DialogProgrammatic.confirm({
          title: 'Send Test Email',
          message: `Are you sure you want to send the test email to ${email}?`,
          onConfirm: async () => await sendTest(template),
        })
      }
    }

    /**
     * @param {Object} template
     * @description Sends a test email using the current email template
     */
    const sendTest = async (users) => {
      const data = template.value
      isSendingTest.value = true

      try {
        await request.post(`/templates/${data.id}/test`, {
          template: data,
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          user: users,
          env: window.env || 'development',
        })

        const msg =
          users.length > 1
            ? `A test email has been sent to selected ${users.length} users.`
            : 'A test email has been sent to selected user.'

        ToastProgrammatic.open({
          message: msg,
          type: 'success',
          position: 'bottom-right',
        })
      } catch (ex) {
        console.error(ex)

        ToastProgrammatic.open({
          message: 'Unable to send test email.',
          type: 'error',
          position: 'bottom-right',
        })
      }

      isSendingTest.value = false
      modalActive.value = false
    }

    /**
     * @param {Object} template
     * @description checks if the template conversion has been saved to the
     * akero database, if it has we will just save without warning, if not
     * then we are going to trigger a dialog warning the user this may
     * affect any existing email campaigns.
     */
    const confirmSaveTemplate = async (template) => {
      if (isConversionComplete.value) {
        await saveTemplate(template)
      } else {
        DialogProgrammatic.confirm({
          title: 'Email Conversion',
          message:
            '<p>You are about to save a v1 email template in the new v2 ' +
            'format, with support for the new drag and drop email builder.' +
            '</p><p>This may adjust some of the existing styling of your ' +
            'email template.</p><p>Are you sure you wish to proceed?</p>',
          onConfirm: async () => await saveTemplate(template),
        })
      }
    }

    /**
     * @param {Object} template
     * @description Sends a request to the Akero API to save the email
     * template with the new data
     */
    const saveTemplate = async (template) => {
      const isValidDetails = await checkTemplateDetails(template)

      const data = { ...template }

      if (!isValidDetails) {
        await handleDetailsError(template)
        console.log('validation failed')
        return
      }

      delete data._id
      delete data.__v
      isSaving.value = true

      if (data.stats) {
        delete data.stats
      }

      try {
        await request.put(`/templates/${template.id}`, data)

        ToastProgrammatic.open({
          message: 'Email template has been updated.',
          type: 'success',
          position: 'bottom-right',
        })

        isConversionComplete.value = true
      } catch (ex) {
        ToastProgrammatic.open({
          message: 'Failed to updated email template.',
          type: 'danger',
          position: 'bottom-right',
        })
      }

      isSaving.value = false
    }

    /**
     * @description Because the form is in a modal window the $ref for it
     * is undefined unless the modal window is open, for this reason we
     * need to have an interval to check for the existance of the form.
     *
     * This will try to check for the form a max of 10 times before it
     * gives up. Once the form has been found the validation method
     * will be executed.
     *
     * If the code within the interval throws, we will just clear the
     * insterval and reject the promise
     */
    const handleDetailsError = () => {
      return new Promise((resolve, reject) => {
        editTemplateDetails.value = true
        const max = 10
        let tries = 0

        const interval = setInterval(() => {
          try {
            if (
              detailsForm.value &&
              typeof detailsForm.value.validate === 'function'
            ) {
              clearInterval(interval)
              detailsForm.value.validate()

              ToastProgrammatic.open({
                message: 'Please ensure all fields are filled in.',
                type: 'danger',
                position: 'bottom-right',
              })
              resolve()
            } else {
              if (tries > max) {
                clearInterval(interval)
                reject(new Error('Max tries reached'))
              } else {
                tries += 1
              }
            }
          } catch (ex) {
            clearInterval(interval)
            reject(ex)
          }
        }, 10)
      })
    }

    onBeforeMount(async () => {
      const res = await fetchUsers()
      users.value = res.data.data

      const res2 = await fetchTemplate(props.id)
      isConversionComplete.value = res2.data.data.version === 2
      template.value = convertTemplate(res2.data.data)
      isLoading.value = false
    })

    return {
      users,
      isLoading,
      template,
      editTemplateDetails,
      isSaving,
      isSendingTest,
      isConversionComplete,
      modalActive,
      onUpdateTemplate,
      fetchUsers,
      fetchTemplate,
      removeChild,
      confirmSendTest,
      sendTest,
      checkTemplateDetails,
      confirmSaveTemplate,
      detailsForm,
    }
  },
})
</script>
